import { message } from "antd";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import "../../../../assets/font/THSarabunNew-normal";
import "../../../../assets/font/THSarabunNew-bold";

const IRRExport = (record, schedule, currencyFormatPoint) => {
  if (!record || !schedule)
    return { handlePrintPDF: () => {}, handleExportExcel: () => {} };

  const fromDate = dayjs(record.first_pay_date).format("DD/MM/YYYY");
  const toDate = dayjs(record.last_pay_date).format("DD/MM/YYYY");
  const createdAt = dayjs(record.created_at).format("DD/MM/YYYY HH:mm:ss");
  const contractNo = record.contract_no ?? "-";
  const fileName = `IRR_${contractNo}_${dayjs(record.created_at).format("YYYYMMDD_HHmm")}`;

  // ── Header columns ─────────────────────────────────────────
  const HEADERS = [
    "งวดที่",
    "วันครบกำหนด",
    "เงินต้นยกมา",
    "ดอกเบี้ย",
    "ตัดเงินต้น",
    "ค่างวด",
    "ยอดชำระจริง",
    "เงินต้นคงเหลือ",
  ];

  // ── Shared row builder ─────────────────────────────────────
  const buildRows = () =>
    schedule.map((row) => ({
      งวดที่: row.period,
      วันครบกำหนด: dayjs(row.due_date).format("DD/MM/YYYY"),
      เงินต้นยกมา: parseFloat(row.opening_balance) || 0,
      ดอกเบี้ย: parseFloat(row.interest) || 0,
      ตัดเงินต้น: parseFloat(row.principal) || 0,
      ค่างวด: parseFloat(row.installment) || 0,
      ยอดชำระจริง: parseFloat(row.actual_payment) || 0,
      เงินต้นคงเหลือ: parseFloat(row.closing_balance) || 0,
    }));

  // ── Export PDF ─────────────────────────────────────────────
  const handlePrintPDF = () => {
    if (!schedule.length) {
      message.warning("ไม่มีข้อมูล");
      return;
    }

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const now = dayjs().format("DD/MM/YYYY HH:mm:ss");

    // ── วาด header แต่ละหน้า ──────────────────────────────────
    const drawHeader = (pageNum) => {
      doc.setFillColor(26, 35, 50);
      doc.rect(0, 0, pageWidth, 36, "F");

      doc.setFont("THSarabunNew", "bold");
      doc.setFontSize(18);
      doc.setTextColor(255, 255, 255);
      doc.text("ตารางคำนวณ IRR Amortization Schedule", pageWidth / 2, 10, {
        align: "center",
      });

      doc.setFont("THSarabunNew", "normal");
      doc.setFontSize(13);
      doc.setTextColor(200, 208, 224);
      doc.text(`สัญญา: ${contractNo}`, pageWidth / 2, 17, { align: "center" });
      doc.text(
        `คำนวณเมื่อ: ${createdAt}   |   งวดแรก: ${fromDate}   |   งวดสุดท้าย: ${toDate}`,
        pageWidth / 2,
        23,
        { align: "center" },
      );

      // summary bar
      doc.setFillColor(52, 73, 94);
      doc.rect(0, 27, pageWidth, 9, "F");
      doc.setFontSize(12);
      doc.setTextColor(200, 208, 224);
      doc.text(
        `Total Interest: ${currencyFormatPoint(record.total_interest_paid)}`,
        8,
        33,
      );
      doc.text(
        `Effective Rate: ${record.effective_rate}%`,
        pageWidth / 2 - 30,
        33,
      );
      doc.text(`Flat Rate: ${record.flat_rate}%`, pageWidth / 2 + 40, 33);
      doc.setTextColor(150, 150, 150);
      doc.text(`หน้าที่ ${pageNum}   |   พิมพ์: ${now}`, pageWidth - 8, 33, {
        align: "right",
      });
    };

    const tableBody = schedule.map((row) => [
      row.period,
      dayjs(row.due_date).format("DD/MM/YYYY"),
      currencyFormatPoint(row.opening_balance),
      currencyFormatPoint(row.interest),
      currencyFormatPoint(row.principal),
      currencyFormatPoint(row.installment),
      currencyFormatPoint(row.actual_payment),
      currencyFormatPoint(row.closing_balance),
    ]);

    // summary row
    const totalInterest = schedule.reduce((s, r) => s + r.interest, 0);
    const totalPrincipal = schedule.reduce((s, r) => s + r.principal, 0);
    const totalInstall = schedule.reduce((s, r) => s + r.installment, 0);
    const totalActual = schedule.reduce((s, r) => s + r.actual_payment, 0);

    const cs = {
      fontStyle: "bold",
      font: "THSarabunNew",
      fillColor: [236, 240, 241],
    };
    const cr = { ...cs, halign: "right" };

    const summaryRow = [
      {
        content: `รวมทั้งหมด ${schedule.length} งวด`,
        colSpan: 2,
        styles: { ...cs, halign: "center" },
      },
      { content: "-", styles: cr },
      { content: currencyFormatPoint(totalInterest), styles: cr },
      { content: currencyFormatPoint(totalPrincipal), styles: cr },
      { content: currencyFormatPoint(totalInstall), styles: cr },
      { content: currencyFormatPoint(totalActual), styles: cr },
      { content: "-", styles: cr },
    ];

    doc.autoTable({
      startY: 40,
      head: [HEADERS],
      body: [...tableBody, summaryRow],
      styles: {
        font: "THSarabunNew",
        fontSize: 11,
        cellPadding: 2,
        halign: "center",
        valign: "middle",
        fillColor: [255, 255, 255],
      },
      headStyles: {
        font: "THSarabunNew",
        fillColor: [44, 62, 80],
        textColor: 255,
        halign: "center",
        fontStyle: "bold",
        fontSize: 12,
        cellPadding: 3,
      },
      columnStyles: {
        0: { cellWidth: 14, halign: "center" },
        1: { cellWidth: 34, halign: "center" },
        2: { halign: "right" },
        3: { halign: "right", textColor: [231, 76, 60] }, // ดอกเบี้ย — แดง
        4: { halign: "right", textColor: [52, 152, 219] }, // ตัดเงินต้น — น้ำเงิน
        5: { halign: "right" },
        6: { halign: "right", fontStyle: "bold" },
        7: { halign: "right" },
      },
      alternateRowStyles: { fillColor: [249, 251, 255] },
      didDrawPage: () =>
        drawHeader(doc.internal.getCurrentPageInfo().pageNumber),
      margin: { top: 40, left: 4, right: 4 },
    });

    doc.save(`${fileName}.pdf`);
    message.success("Export PDF สำเร็จ");
  };

  // ── Export Excel ───────────────────────────────────────────
  const handleExportExcel = () => {
    if (!schedule.length) {
      message.warning("ไม่มีข้อมูล");
      return;
    }

    const rows = buildRows();

    // summary row
    const totalInterest = schedule.reduce((s, r) => s + r.interest, 0);
    const totalPrincipal = schedule.reduce((s, r) => s + r.principal, 0);
    const totalInstall = schedule.reduce((s, r) => s + r.installment, 0);
    const totalActual = schedule.reduce((s, r) => s + r.actual_payment, 0);

    const summaryRow = {
      งวดที่: "",
      วันครบกำหนด: `รวม ${schedule.length} งวด`,
      เงินต้นยกมา: "",
      ดอกเบี้ย: totalInterest,
      ตัดเงินต้น: totalPrincipal,
      ค่างวด: totalInstall,
      ยอดชำระจริง: totalActual,
      เงินต้นคงเหลือ: "",
    };

    const allRows = [...rows, summaryRow];
    const ws = XLSX.utils.json_to_sheet(allRows, { header: HEADERS });

    // ── column widths ──────────────────────────────────────
    ws["!cols"] = [
      { wch: 8 },
      { wch: 18 },
      { wch: 16 },
      { wch: 14 },
      { wch: 14 },
      { wch: 14 },
      { wch: 16 },
      { wch: 16 },
    ];

    const MONEY_COLS = ["C", "D", "E", "F", "G", "H"]; // เงินต้นยกมา → คงเหลือ
    const totalRows = allRows.length + 1; // +1 = header row

    // ── styles ──────────────────────────────────────────────
    const border = {
      top: { style: "thin", color: { rgb: "CCCCCC" } },
      bottom: { style: "thin", color: { rgb: "CCCCCC" } },
      left: { style: "thin", color: { rgb: "CCCCCC" } },
      right: { style: "thin", color: { rgb: "CCCCCC" } },
    };

    // Header row
    HEADERS.forEach((_, ci) => {
      const ref = XLSX.utils.encode_cell({ r: 0, c: ci });
      if (!ws[ref]) return;
      ws[ref].s = {
        fill: { patternType: "solid", fgColor: { rgb: "1A2332" } },
        font: { bold: true, sz: 12, color: { rgb: "C8D0E0" } },
        alignment: { horizontal: "center", vertical: "center" },
        border,
      };
    });

    // Data rows
    rows.forEach((_, ri) => {
      const isEven = ri % 2 === 0;
      HEADERS.forEach((key, ci) => {
        const ref = XLSX.utils.encode_cell({ r: ri + 1, c: ci });
        if (!ws[ref]) return;
        const isMoney = ci >= 2;
        const isInt = ci === 3; // ดอกเบี้ย
        const isPrin = ci === 4; // ตัดเงินต้น
        ws[ref].s = {
          fill: {
            patternType: "solid",
            fgColor: { rgb: isEven ? "FFFFFF" : "F9FBFF" },
          },
          font: {
            sz: 11,
            color: { rgb: isInt ? "E74C3C" : isPrin ? "3498DB" : "333333" },
            bold: ci === 6,
          },
          alignment: {
            horizontal: isMoney ? "right" : ci === 0 ? "center" : "left",
            vertical: "center",
          },
          border,
          numFmt: isMoney ? "#,##0.00" : undefined,
        };
      });
    });

    // Summary row
    const sumIdx = rows.length + 1;
    HEADERS.forEach((_, ci) => {
      const ref = XLSX.utils.encode_cell({ r: sumIdx, c: ci });
      if (!ws[ref]) return;
      const isMoney = ci >= 2;
      ws[ref].s = {
        fill: { patternType: "solid", fgColor: { rgb: "D5E8D4" } },
        font: { bold: true, sz: 12 },
        alignment: {
          horizontal: isMoney ? "right" : "center",
          vertical: "center",
        },
        border,
        numFmt: isMoney ? "#,##0.00" : undefined,
      };
    });

    // ── info rows ด้านบน (prepend 3 บรรทัด) ─────────────────
    // เพิ่ม metadata ไว้บนสุด
    XLSX.utils.sheet_add_aoa(
      ws,
      [
        [`สัญญา: ${contractNo}   |   คำนวณเมื่อ: ${createdAt}`],
        [
          `Effective Rate: ${record.effective_rate}%   |   Flat Rate: ${record.flat_rate}%   |   Total Interest: ${currencyFormatPoint(record.total_interest_paid)}`,
        ],
        [],
      ],
      { origin: "A1", sheetRows: 0 },
    );

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "IRR Schedule");
    XLSX.writeFile(wb, `${fileName}.xlsx`);
    message.success("Export Excel สำเร็จ");
  };

  return { handlePrintPDF, handleExportExcel };
};

export default IRRExport;
